import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux/es/exports";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";
import ModalPrice from "../../components/product/ModalPrice";
import { BsArrowBarRight } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import DeleteButton from "../../components/DeleteButton";
import { createUnits } from "../../services/unitService";
import Pagination from "../../components/Pagination";
import { deleteFilling } from "../../services/fillingService";
import { toast } from "react-toastify";
import EditButton from "../../components/EditButton";
import TableComponent from "../../components/tableComponent/tableComponent";
import Select from "../../components/Select";
import SelectComponent from "../../components/SelectComponent";
import saveItem, { getList, getItem } from "../../services/service";
import { MdCheck, MdClose, MdContentCopy } from "react-icons/md";

function NewPriceLayoutV2({ selectedId, groupType }) {
  const [rowId, setRowId] = useState(null);
  const { token } = useSelector((state) => state.user);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [itemOffset, setItemOffset] = useState(0);
  const [forcePage, setForcePage] = useState(0);
  const [countries, setCountries] = useState([]);
  const [textures, setTextures] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState(null);

  const { name } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const headerTable = [
    { name: "textures" },
    { name: "date" },
    { name: "price" },
    { name: "priceOfOne" },
    { name: "countryCode" },
    { name: "actions" },
  ];

  const bodyTable = ["colors", "date", "price", "priceOfOne", "countryCode", "actions"];

  const selectedProductName = rawData.length > 0 ? rawData[0].productName : "";

  const fetchCountries = async () => {
    const response = await getList(
      `${process.env.REACT_APP_HOST_BASE_URL}/v1/country/list`,
      token
    );
    if (response?.data) setCountries(response.data);
  };

  const fetchTextures = async () => {
    const response = await getList(
      `${process.env.REACT_APP_HOST_BASE_URL}/v1/texture/list`,
      token
    );
    if (response?.data)
      setTextures(response.data.map((item) => ({ id: item.id, name: item.name })));
  };

  const fetchCurrencies = async () => {
    const response = await getList(
      `${process.env.REACT_APP_HOST_BASE_URL}/v1/currency/list`,
      token
    );
    if (response?.data) setCurrencies(response.data);
  };

  const fetchData = async () => {
    const filter = { productId: selectedId };
    if (selectedCountryId) filter.countryId = selectedCountryId;

    const response = await createUnits(
      `${process.env.REACT_APP_HOST_BASE_URL}/v1/product/price/table`,
      token,
      {
        draw: 1,
        start: itemOffset,
        length: itemsPerPage,
        recordsTotal: 0,
        columns: [],
        order: [],
        search: { value: "", regex: true },
        filter,
      }
    );

    if (selectedId && response.success) {
      setIsLoading(false);
      setRawData(response.data.data);
      setRecordsTotal(response.data.recordsFiltered);
    } else {
      setIsLoading(false);
      navigate("/error", {
        state: {
          status: response.status,
          message: response.message ? response.message : null,
        },
      });
    }
  };

  const handleEditClick = async (id) => {
    const linkedItem = rawData.find((row) => row.id === id);
    if (linkedItem?.akfaExternalName) {
      toast.warn(t("akfa_price_link_warning", { name: linkedItem.akfaExternalName }), {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
    const response = await getItem(
      `${process.env.REACT_APP_HOST_BASE_URL}/v1/product/price/${id}`,
      token
    );
    if (response?.success) {
      setEditingRowId(id);
      setEditingData({
        id: response.data.id,
        productId: response.data.productId,
        textureId: response.data.textureId,
        currencyId: response.data.currencyId,
        countryId: response.data.countryId,
        price: response.data.price,
        priceOfOne: response.data.priceOfOne ?? "",
        date: response.data.date ? response.data.date.substring(0, 10) : "",
      });
    } else {
      toast.error(t("action.error"), { position: "bottom-right", autoClose: 3000 });
    }
  };

  const handleInlineSave = async () => {
    const res = await saveItem(
      `${process.env.REACT_APP_HOST_BASE_URL}/v1/product/price`,
      token,
      editingData
    );
    if (res.success) {
      setEditingRowId(null);
      setEditingData(null);
      fetchData();
      toast.success(t("action.edit_success"), {
        position: "bottom-right",
        autoClose: 3000,
        closeOnClick: true,
        className: "add-success",
      });
    } else {
      toast.error(t("action.error"), { position: "bottom-right", autoClose: 3000 });
    }
  };

  const handleInlineCancel = () => {
    setEditingRowId(null);
    setEditingData(null);
  };

  const handleCopyItem = async (id) => {
    const response = await fetch(
      `${process.env.REACT_APP_HOST_BASE_URL}/v1/product/price/copy/${id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const data = await response.json();
    if (response.ok && data.success !== false) {
      fetchData();
      toast.success(t("action.copy_success") || "Copied", {
        position: "bottom-right",
        autoClose: 3000,
        closeOnClick: true,
        className: "add-success",
      });
    } else {
      toast.error(t("action.error"), { position: "bottom-right", autoClose: 3000 });
    }
  };

  const onDeleteItem = async (id) => {
    const confirmed = await swal({
      title: t("action.delete_item.title"),
      text: t("action.delete_item.text"),
      icon: "warning",
      buttons: { cancel: true, confirm: true },
    });
    if (!confirmed) return;

    setIsLoading(true);
    const response = await deleteFilling(
      `${process.env.REACT_APP_HOST_BASE_URL}/v1/product/price/${id}`,
      token
    );
    if (response.success) {
      setIsLoading(false);
      fetchData();
      toast.success(t("action.delete_success"), {
        position: "bottom-right",
        autoClose: 3000,
        closeOnClick: true,
        className: "add-success",
      });
    } else {
      setIsLoading(false);
      navigate("/error", {
        state: {
          status: response.status,
          message: response.message ? response.message : null,
        },
      });
    }
  };

  const displayData = useMemo(() => {
    return rawData.map((item) => {
      const isEditing = item.id === editingRowId;

      if (isEditing) {
        const selectCls =
          "border border-gray-300 rounded px-1 py-1 text-sm w-full bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500";
        return {
          id: item.id,
          selected: false,
          colors: (
            <select
              value={editingData.textureId ?? ""}
              onChange={(e) =>
                setEditingData((prev) => ({ ...prev, textureId: Number(e.target.value) }))
              }
              onClick={(e) => e.stopPropagation()}
              className={selectCls}
            >
              <option value="" disabled />
              {textures.map((tx) => (
                <option key={tx.id} value={tx.id}>{tx.name}</option>
              ))}
            </select>
          ),
          date: (
            <input
              type="date"
              value={editingData.date}
              onChange={(e) =>
                setEditingData((prev) => ({ ...prev, date: e.target.value }))
              }
              onClick={(e) => e.stopPropagation()}
              className="border border-gray-300 rounded px-1 py-1 text-sm w-full"
            />
          ),
          price: (
            <div
              className="flex items-center gap-1 min-w-[160px]"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="number"
                value={editingData.price}
                onChange={(e) =>
                  setEditingData((prev) => ({ ...prev, price: e.target.value }))
                }
                className="border border-gray-300 rounded px-1 py-1 text-sm w-full"
              />
              <select
                value={editingData.currencyId ?? ""}
                onChange={(e) =>
                  setEditingData((prev) => ({ ...prev, currencyId: Number(e.target.value) }))
                }
                className={selectCls + " min-w-[60px]"}
              >
                <option value="" disabled />
                {currencies.map((c) => (
                  <option key={c.id} value={c.id}>{c.symbol ?? c.name}</option>
                ))}
              </select>
            </div>
          ),
          priceOfOne: (
            <input
              type="number"
              value={editingData.priceOfOne}
              onChange={(e) =>
                setEditingData((prev) => ({ ...prev, priceOfOne: e.target.value }))
              }
              onClick={(e) => e.stopPropagation()}
              className="border border-gray-300 rounded px-1 py-1 text-sm w-full"
            />
          ),
          countryCode: (
            <select
              value={editingData.countryId ?? ""}
              onChange={(e) =>
                setEditingData((prev) => ({ ...prev, countryId: Number(e.target.value) }))
              }
              onClick={(e) => e.stopPropagation()}
              className={selectCls}
            >
              <option value="" disabled />
              {countries.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          ),
          actions: (
            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={handleInlineSave}
                className="p-1 text-white bg-green-600 rounded hover:bg-green-700"
                title={t("save")}
              >
                <MdCheck className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleInlineCancel}
                className="p-1 text-white bg-gray-400 rounded hover:bg-gray-500"
                title={t("cancel")}
              >
                <MdClose className="w-4 h-4" />
              </button>
            </div>
          ),
        };
      }

      return {
        id: item.id,
        selected: false,
        colors: (
          <div className="flex items-center">
            <div>{item.textureName}</div>
            {item.akfaExternalName && (
              <span
                title={t("akfa_price_link_title", { name: item.akfaExternalName })}
                className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700"
              >
                Akfa
              </span>
            )}
            <div className="ml-3">
              {item.imageId ? (
                <img
                  className="object-cover w-6 h-6 rounded-full"
                  src={`${process.env.REACT_APP_HOST_BASE_URL}/v1/file/texture/${item.imageId}`}
                  alt="img"
                />
              ) : (
                <div
                  style={{ backgroundColor: `${item.color}` }}
                  className="py-1 rounded-lg text-white px-2"
                >
                  {item.color}
                </div>
              )}
            </div>
          </div>
        ),
        date: item.date,
        price: item.price + " " + item?.currencySymbol,
        priceOfOne: item.priceOfOne ? item.priceOfOne + " " + item?.currencySymbol : "",
        countryCode: item.countryCode,
        actions: (
          <div className="flex items-center gap-1">
            <EditButton onclick={() => handleEditClick(item.id)} />
            <DeleteButton onclick={() => onDeleteItem(item.id)} />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleCopyItem(item.id); }}
              className="p-1 text-white bg-blue-500 rounded hover:bg-blue-600"
              title={t("action.copy") || "Copy"}
            >
              <MdContentCopy className="w-4 h-4" />
            </button>
          </div>
        ),
      };
    });
  }, [rawData, editingRowId, editingData, textures, currencies, countries]);

  const pageCount = Math.ceil(recordsTotal / parseInt(itemsPerPage));

  useEffect(() => {
    fetchCountries();
    fetchTextures();
    fetchCurrencies();
  }, []);

  useEffect(() => {
    fetchData();
  }, [itemOffset, itemsPerPage, recordsTotal, selectedId, selectedCountryId]);

  useEffect(() => {
    setItemOffset(0);
    setForcePage(0);
  }, [selectedId]);

  const handlePageClick = (event) => {
    setForcePage(event.selected);
    const newOffset = (event.selected * parseInt(itemsPerPage)) % recordsTotal;
    setItemOffset(newOffset);
  };

  return (
    <>
      <ModalPrice
        productId={selectedId}
        groupType={groupType}
        productName={name}
        rowId={rowId}
        open={open}
        setOpen={setOpen}
        fetchData={fetchData}
        selectedCountryId={selectedCountryId}
      />
      <div>
        <div>
          <div className="text-2xl font-semibold">{selectedProductName}</div>
        </div>
        <div className="mb-2 sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-semibold text-gray-900">
              {t("table.product_price")}
            </h1>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-4 flex items-center gap-2">
            <div className="w-48">
              <SelectComponent
                key={selectedCountryId ?? "empty"}
                data={countries}
                title={t("filter_by_country")}
                item={countries.find((c) => c.id === selectedCountryId) || null}
                onchange={(value) => setSelectedCountryId(value.id)}
              />
            </div>
            {selectedCountryId && (
              <button
                type="button"
                className="text-sm text-gray-500 hover:text-gray-700 underline"
                onClick={() => setSelectedCountryId(null)}
              >
                {t("clear")}
              </button>
            )}
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-4 sm:flex-none">
            <button
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
              onClick={() => {
                setOpen(true);
                setRowId(null);
              }}
            >
              {t("add_price")}
              <BsArrowBarRight className="w-6 h-6 ml-1" />
            </button>
          </div>
        </div>
        {isLoading ? (
          <h1>Loading...</h1>
        ) : (
          <div>
            <TableComponent
              transactions={displayData}
              headerOfTable={headerTable}
              bodyOfTable={bodyTable}
              rowHandler={() => {}}
            />
            <div className="flex justify-between mt-2 items-center">
              <Select setItemsPerPage={setItemsPerPage} itemsPerPage={itemsPerPage} />
              <Pagination
                handlePageClick={handlePageClick}
                pageCount={pageCount}
                forcePage={forcePage}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default NewPriceLayoutV2;
